import { useCallback, useEffect, useRef, useState } from 'react';
import { ReactSketchCanvas } from 'react-sketch-canvas';
import MarkdownRenderer from '../MarkdownRenderer';
import useModalFocusTrap from '../useModalFocusTrap';

const INTERACTION_MODE = {
  DRAW: 'DRAW',
  SCROLL: 'SCROLL',
};

// Keep canvas answers above the backend OCR warning floor after its 2200px
// max-edge normalization, while retaining headroom below the 40MP upload cap.
export const DRAWING_EXPORT_MIN_EDGE = 704;
export const DRAWING_EXPORT_NORMALIZED_MAX_EDGE = 2200;
export const DRAWING_EXPORT_MAX_PIXELS = 39_000_000;

export function getDrawingExportDimensions(sourceWidth, sourceHeight, devicePixelRatio = 1) {
  if (sourceWidth <= 0 || sourceHeight <= 0) {
    return null;
  }

  const requestedScale = Math.max(
    1,
    devicePixelRatio || 1,
    DRAWING_EXPORT_MIN_EDGE / Math.min(sourceWidth, sourceHeight)
  );
  let contentWidth = Math.ceil(sourceWidth * requestedScale);
  let contentHeight = Math.ceil(sourceHeight * requestedScale);

  // The backend constrains the longest edge to 2200px. Pad extreme aspect
  // ratios so that normalization cannot shrink the shorter edge below 704px.
  let width = Math.max(
    contentWidth,
    Math.ceil((contentHeight * DRAWING_EXPORT_MIN_EDGE) / DRAWING_EXPORT_NORMALIZED_MAX_EDGE)
  );
  let height = Math.max(
    contentHeight,
    Math.ceil((contentWidth * DRAWING_EXPORT_MIN_EDGE) / DRAWING_EXPORT_NORMALIZED_MAX_EDGE)
  );

  const pixelLimitScale = Math.min(
    1,
    Math.sqrt(DRAWING_EXPORT_MAX_PIXELS / (width * height))
  );
  if (pixelLimitScale < 1) {
    contentWidth = Math.floor(contentWidth * pixelLimitScale);
    contentHeight = Math.floor(contentHeight * pixelLimitScale);
    width = Math.floor(width * pixelLimitScale);
    height = Math.floor(height * pixelLimitScale);
  }

  return {
    width,
    height,
    contentWidth,
    contentHeight,
    offsetX: Math.floor((width - contentWidth) / 2),
    offsetY: Math.floor((height - contentHeight) / 2),
  };
}

export async function scaleDrawingExport(dataUrl, devicePixelRatio = window.devicePixelRatio || 1) {
  const image = await new Promise((resolve, reject) => {
    const loadedImage = new Image();
    loadedImage.onload = () => resolve(loadedImage);
    loadedImage.onerror = reject;
    loadedImage.src = dataUrl;
  });
  const dimensions = getDrawingExportDimensions(
    image.naturalWidth,
    image.naturalHeight,
    devicePixelRatio
  );
  if (
    dimensions.width === image.naturalWidth &&
    dimensions.height === image.naturalHeight &&
    dimensions.contentWidth === image.naturalWidth &&
    dimensions.contentHeight === image.naturalHeight
  ) {
    return dataUrl;
  }

  const exportCanvas = document.createElement('canvas');
  exportCanvas.width = dimensions.width;
  exportCanvas.height = dimensions.height;
  const context = exportCanvas.getContext('2d');
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, dimensions.width, dimensions.height);
  context.drawImage(
    image,
    dimensions.offsetX,
    dimensions.offsetY,
    dimensions.contentWidth,
    dimensions.contentHeight
  );
  return exportCanvas.toDataURL('image/png');
}

async function dataUrlToFile(dataUrl, fileName) {
  const response = await fetch(dataUrl);
  const blob = await response.blob();
  return new File([blob], fileName, { type: 'image/png' });
}

function createBlankPage(id) {
  return {
    id,
    paths: [],
    file: null,
    previewUrl: '',
  };
}

function revokePagePreview(page) {
  if (page?.previewUrl?.startsWith('blob:')) {
    URL.revokeObjectURL(page.previewUrl);
  }
}

function getViewportProfile() {
  if (typeof window === 'undefined') {
    return {
      canvasHeight: 480,
      isConstrained: false,
      usesCompactTools: false,
    };
  }

  const width = window.innerWidth;
  const height = window.innerHeight;

  if (height <= 440) {
    return {
      canvasHeight: 220,
      isConstrained: true,
      usesCompactTools: true,
    };
  }

  if (width <= 640 || height <= 620) {
    return {
      canvasHeight: 280,
      isConstrained: true,
      usesCompactTools: height <= 500,
    };
  }

  if (width <= 1024 || height <= 760) {
    return {
      canvasHeight: 360,
      isConstrained: width <= 860 || height <= 760,
      usesCompactTools: false,
    };
  }

  return {
    canvasHeight: 480,
    isConstrained: false,
    usesCompactTools: false,
  };
}

const DISCARD_DRAWING_WARNING = 'Discard this in-progress drawing?';

function DrawingCanvasModal({
  isOpen,
  onClose,
  onSave,
  onDraftChange,
  initialDraftPages = null,
  questionMarkdown = '',
  restoreFocusRef,
}) {
  const canvasRef = useRef(null);
  const modalRef = useRef(null);
  const closeButtonRef = useRef(null);
  const closeInFlightRef = useRef(false);
  const initialDraftPagesRef = useRef(initialDraftPages);
  const nextPageIdRef = useRef(1);
  const [isSaving, setIsSaving] = useState(false);
  const [pages, setPages] = useState([]);
  const [activePageIndex, setActivePageIndex] = useState(0);
  const [isEraser, setIsEraser] = useState(false);
  const [interactionMode, setInteractionMode] = useState(INTERACTION_MODE.DRAW);
  const [canvasHeight, setCanvasHeight] = useState(() => getViewportProfile().canvasHeight);
  const [isConstrainedViewport, setIsConstrainedViewport] = useState(
    () => getViewportProfile().isConstrained
  );
  const [isPageStripOpen, setIsPageStripOpen] = useState(() => !getViewportProfile().isConstrained);
  const [usesCompactTools, setUsesCompactTools] = useState(
    () => getViewportProfile().usesCompactTools
  );
  const [isToolsTrayOpen, setIsToolsTrayOpen] = useState(false);
  const [canvasError, setCanvasError] = useState('');
  const isScrollMode = interactionMode === INTERACTION_MODE.SCROLL;
  const isDrawMode = interactionMode === INTERACTION_MODE.DRAW;

  useModalFocusTrap({
    active: isOpen,
    containerRef: modalRef,
    initialFocusRef: closeButtonRef,
    restoreFocusRef,
  });

  if (!isOpen) {
    initialDraftPagesRef.current = initialDraftPages;
  }

  useEffect(() => {
    if (isOpen) {
      closeInFlightRef.current = false;
      const viewport = getViewportProfile();
      const restoredPages = Array.isArray(initialDraftPagesRef.current)
        ? initialDraftPagesRef.current.filter(Array.isArray)
        : [];
      const seededPages = restoredPages.length
        ? restoredPages.map((paths, index) => ({ ...createBlankPage(index + 1), paths }))
        : [createBlankPage(1)];
      nextPageIdRef.current = seededPages.length + 1;
      setPages(seededPages);
      setActivePageIndex(0);
      setIsEraser(false);
      setInteractionMode(INTERACTION_MODE.DRAW);
      setCanvasHeight(viewport.canvasHeight);
      setIsConstrainedViewport(viewport.isConstrained);
      setIsPageStripOpen(!viewport.isConstrained);
      setUsesCompactTools(viewport.usesCompactTools);
      setIsToolsTrayOpen(false);
      setCanvasError('');
      return;
    }
    setPages((prev) => {
      prev.forEach(revokePagePreview);
      return [];
    });
  }, [isOpen]);

  useEffect(
    () => () => {
      pages.forEach(revokePagePreview);
    },
    [pages]
  );

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const syncViewport = () => {
      const viewport = getViewportProfile();
      setCanvasHeight(viewport.canvasHeight);
      setUsesCompactTools(viewport.usesCompactTools);
      setIsConstrainedViewport((previous) => {
        if (previous !== viewport.isConstrained) {
          setIsPageStripOpen(!viewport.isConstrained);
        }
        return viewport.isConstrained;
      });
      setIsToolsTrayOpen((previous) => (viewport.usesCompactTools ? previous : false));
    };

    syncViewport();
    window.addEventListener('resize', syncViewport);
    return () => window.removeEventListener('resize', syncViewport);
  }, [isOpen]);

  // Load the active page into the canvas ONLY when the active page actually
  // changes. This effect depends on `pages`, so every state update that
  // replaces that array re-runs it -- and resetCanvas() wipes whatever the
  // student is drawing right now. Publishing a draft replaces `pages`, so a
  // stroke could erase itself. That is the data loss this whole PR exists to
  // stop, so the guard is the point, not an optimisation.
  const loadedPageKeyRef = useRef(null);
  useEffect(() => {
    if (!isOpen) {
      loadedPageKeyRef.current = null;
      return;
    }
    const activePage = pages[activePageIndex];
    if (!activePage || !canvasRef.current) {
      return;
    }
    const pageKey = `${activePageIndex}:${activePage.id}`;
    if (loadedPageKeyRef.current === pageKey) {
      return;
    }
    loadedPageKeyRef.current = pageKey;
    canvasRef.current.resetCanvas?.();
    if (Array.isArray(activePage.paths) && activePage.paths.length) {
      canvasRef.current.loadPaths?.(activePage.paths);
    }
  }, [activePageIndex, isOpen, pages]);

  useEffect(() => {
    if (!isOpen || !canvasRef.current) {
      return;
    }
    canvasRef.current.eraseMode?.(isEraser);
  }, [isEraser, isOpen]);

  const exportCurrentCanvasAsPage = async (existingPage) => {
    const paths = await canvasRef.current?.exportPaths?.();
    if (!Array.isArray(paths) || paths.length === 0) {
      return null;
    }
    const rawDataUrl = await canvasRef.current?.exportImage('png');
    const dataUrl = rawDataUrl ? await scaleDrawingExport(rawDataUrl) : null;
    if (!dataUrl) {
      return null;
    }
    const file = await dataUrlToFile(dataUrl, `drawing-${Date.now()}.png`);
    return {
      id: existingPage?.id ?? nextPageIdRef.current,
      paths,
      file,
      previewUrl: URL.createObjectURL(file),
    };
  };

  const replacePage = (collection, index, nextPage) => {
    const next = [...collection];
    const previous = next[index];
    if (previous?.previewUrl && previous.previewUrl !== nextPage.previewUrl) {
      revokePagePreview(previous);
    }
    next[index] = nextPage;
    return next;
  };

  const buildPersistedPages = async () => {
    const currentPage = pages[activePageIndex] || createBlankPage(nextPageIdRef.current);
    const currentSnapshot = await exportCurrentCanvasAsPage(currentPage);
    return pages.map((page, index) => {
      if (index !== activePageIndex) {
        return page;
      }
      return currentSnapshot || createBlankPage(page.id);
    });
  };

  const publishDraft = useCallback(async (nextPages = pages, nextActivePageIndex = activePageIndex) => {
    const activePaths = await canvasRef.current?.exportPaths?.();
    const draftPages = nextPages.map((page, index) =>
      index === nextActivePageIndex && Array.isArray(activePaths) ? activePaths : page.paths || []
    );
    onDraftChange?.(draftPages.some((paths) => paths.length) ? draftPages : []);
    return draftPages;
  }, [activePageIndex, onDraftChange, pages]);

  const handleClose = useCallback(async () => {
    if (closeInFlightRef.current) return;
    closeInFlightRef.current = true;
    try {
      const draftPages = await publishDraft();
      if (draftPages.some((paths) => paths.length) && !window.confirm(DISCARD_DRAWING_WARNING)) {
        closeInFlightRef.current = false;
        return;
      }
      onDraftChange?.([]);
      onClose();
    } catch (_error) {
      closeInFlightRef.current = false;
      setCanvasError('Could not close the drawing workspace. Please try again.');
    }
  }, [onClose, onDraftChange, publishDraft]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && !isSaving) {
        event.preventDefault();
        void handleClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [handleClose, isOpen, isSaving]);

  const handleDoneDrawing = async () => {
    try {
      setIsSaving(true);
      setCanvasError('');

      const allPages = await buildPersistedPages();
      const savedPages = allPages.filter((page) => Array.isArray(page.paths) && page.paths.length);

      if (savedPages.length === 0) {
        setCanvasError('Drawing is blank — write something before submitting.');
        return;
      }

      onSave(savedPages.map((page) => page.file), savedPages.map((page) => page.previewUrl));
      onDraftChange?.([]);
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddPage = async () => {
    setCanvasError('');
    try {
      const currentPage = pages[activePageIndex] || createBlankPage(nextPageIdRef.current);
      const snapshot = await exportCurrentCanvasAsPage(currentPage);
      if (!snapshot) {
        setCanvasError('Drawing is blank — write something before adding a page.');
        return;
      }
      const blankPage = createBlankPage(nextPageIdRef.current);
      nextPageIdRef.current += 1;
      setPages((prev) => {
        const withSavedCurrent = replacePage(prev, activePageIndex, snapshot);
        const next = [...withSavedCurrent, blankPage];
        onDraftChange?.(next.map((page) => page.paths || []));
        return next;
      });
      setActivePageIndex(pages.length);
      setCanvasError('');
    } catch (_error) {
      setCanvasError('Could not add page. Please try again.');
    }
  };

  const handleSelectPage = async (index) => {
    if (index === activePageIndex || isSaving) {
      return;
    }
    const nextPages = await buildPersistedPages();
    onDraftChange?.(nextPages.map((page) => page.paths || []));
    setPages(nextPages);
    setActivePageIndex(index);
    setCanvasError('');
  };

  const handleRemovePage = (index) => {
    if (isSaving) {
      return;
    }
    setPages((prev) => {
      const next = [...prev];
      const removed = next.splice(index, 1)[0];
      revokePagePreview(removed);
      if (!next.length) {
        nextPageIdRef.current += 1;
        onDraftChange?.([]);
        return [createBlankPage(nextPageIdRef.current - 1)];
      }
      onDraftChange?.(next.map((page) => page.paths || []));
      return next;
    });
    setActivePageIndex((current) => {
      if (pages.length <= 1) {
        return 0;
      }
      if (current === index) {
        return Math.max(0, Math.min(index, pages.length - 2));
      }
      if (current > index) {
        return current - 1;
      }
      return current;
    });
  };

  if (!isOpen) {
    return null;
  }

  const showFullToolbar = !usesCompactTools;
  const showToolsTray = showFullToolbar || isToolsTrayOpen;
  const currentModeLabel = isScrollMode ? 'Scroll mode' : isEraser ? 'Eraser on' : 'Draw mode';

  const toolbarContent = (
    <div className="canvas-toolbar" data-testid="drawing-toolbar">
      <button
        type="button"
        className={`btn btn-tertiary ${isDrawMode ? 'is-active' : ''}`}
        aria-pressed={isDrawMode}
        disabled={isSaving}
        onClick={() => setInteractionMode(INTERACTION_MODE.DRAW)}
      >
        Draw
      </button>
      <button
        type="button"
        className={`btn btn-tertiary ${isScrollMode ? 'is-active' : ''}`}
        aria-pressed={isScrollMode}
        disabled={isSaving}
        onClick={() => setInteractionMode(INTERACTION_MODE.SCROLL)}
      >
        Scroll
      </button>
      <button
        type="button"
        className="btn btn-tertiary"
        disabled={isSaving || isScrollMode}
        onClick={() => canvasRef.current?.undo?.()}
      >
        Undo
      </button>
      <button
        type="button"
        className={`btn btn-tertiary ${isEraser ? 'is-active' : ''}`}
        aria-pressed={isEraser}
        disabled={isSaving || isScrollMode}
        onClick={async () => {
          const next = !isEraser;
          setIsEraser(next);
          await canvasRef.current?.eraseMode?.(next);
        }}
      >
        Eraser
      </button>
      <button
        type="button"
        className="btn btn-tertiary"
        onClick={async () => {
          await canvasRef.current?.clearCanvas?.();
          const next = pages.map((page, index) => index === activePageIndex ? { ...page, paths: [] } : page);
          onDraftChange?.(next.some((page) => page.paths?.length) ? next.map((page) => page.paths || []) : []);
        }}
        disabled={isSaving || isScrollMode}
      >
        Clear
      </button>
      <button
        type="button"
        className="btn btn-secondary"
        disabled={isSaving}
        onClick={handleAddPage}
      >
        Add page
      </button>
      <button
        type="button"
        className="btn btn-tertiary"
        disabled={isSaving}
        onClick={() => setCanvasHeight((prev) => prev + 320)}
      >
        Add space
      </button>
    </div>
  );

  const pagesContent = (
    <>
      <div className="canvas-pages-meta">
        <button
          type="button"
          className={`btn btn-tertiary canvas-pages-toggle${isPageStripOpen ? ' is-active' : ''}`}
          onClick={() => setIsPageStripOpen((previous) => !previous)}
          aria-expanded={isPageStripOpen}
        >
          Pages ({pages.length})
        </button>
        <span>Current page: {activePageIndex + 1}</span>
      </div>

      {isPageStripOpen ? (
        <div
          className={`canvas-page-strip${isConstrainedViewport ? ' is-constrained' : ''}`}
          role="tablist"
          aria-label="Drawing pages"
          data-testid="drawing-page-strip"
        >
          {pages.map((page, index) => {
            const isActivePage = index === activePageIndex;
            return (
              <div
                className={`canvas-page-chip${isActivePage ? ' is-active' : ''}`}
                key={page.id}
              >
                <button
                  type="button"
                  className={`canvas-page-select${isActivePage ? ' is-active' : ''}`}
                  onClick={() => handleSelectPage(index)}
                  aria-pressed={isActivePage}
                  aria-label={`Select page ${index + 1}`}
                >
                  {page.previewUrl ? (
                    <img src={page.previewUrl} alt={`Drawing page ${index + 1}`} />
                  ) : (
                    <div className="canvas-page-placeholder">
                      {isActivePage ? 'Editing' : 'Blank'}
                    </div>
                  )}
                  <span className="canvas-page-number">Page {index + 1}</span>
                </button>
                <button
                  type="button"
                  className="btn btn-tertiary canvas-page-remove-btn"
                  disabled={isSaving}
                  onClick={() => handleRemovePage(index)}
                  aria-label={`Remove page ${index + 1}`}
                >
                  Remove
                </button>
              </div>
            );
          })}
        </div>
      ) : null}
    </>
  );

  return (
    <div
      ref={modalRef}
      className="canvas-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="Draw answer"
    >
      <div className="canvas-modal-sheet">
        <div className="canvas-modal-header">
          <h3>Draw answer</h3>
          <button
            ref={closeButtonRef}
            type="button"
            className="btn btn-tertiary"
            onClick={handleClose}
            disabled={isSaving}
          >
            Close
          </button>
        </div>

        {questionMarkdown ? (
          <details className="canvas-question-context" open>
            <summary>Question</summary>
            <div className="markdown-block"><MarkdownRenderer content={questionMarkdown} /></div>
          </details>
        ) : null}

        <div className={`canvas-modal-body${questionMarkdown ? '' : ' canvas-modal-body-no-question'}`}>
          {showFullToolbar ? (
            toolbarContent
          ) : (
            <div className="canvas-compact-tools-bar" data-testid="drawing-compact-tools">
              <span className="canvas-current-mode">{currentModeLabel}</span>
              <button
                type="button"
                className={`btn btn-tertiary canvas-tools-toggle${isToolsTrayOpen ? ' is-active' : ''}`}
                onClick={() => setIsToolsTrayOpen((previous) => !previous)}
                aria-expanded={isToolsTrayOpen}
              >
                Tools
              </button>
            </div>
          )}

          {showToolsTray ? (
            <div className={showFullToolbar ? undefined : 'canvas-tools-tray'} data-testid="drawing-tools-tray">
              {!showFullToolbar ? toolbarContent : null}
              {pagesContent}
            </div>
          ) : null}

          <div
            className={`canvas-scroll-shell ${isDrawMode ? 'is-draw-mode' : 'is-scroll-mode'} is-compact`}
            style={{ maxHeight: `${canvasHeight}px` }}
            data-testid="drawing-scroll-shell"
          >
            <div className="canvas-frame" style={{ minHeight: `${canvasHeight}px` }}>
              <div
                className={`drawing-canvas-interaction-layer ${isDrawMode ? 'is-draw-mode' : 'is-scroll-mode'}`}
                style={{ pointerEvents: isDrawMode ? 'auto' : 'none' }}
                role="group"
                aria-label="Drawing surface. Use pointer or touch to write your answer."
              >
                <ReactSketchCanvas
                  ref={canvasRef}
                  className={`drawing-canvas-surface ${isDrawMode ? 'is-draw-mode' : 'is-scroll-mode'}`}
                  strokeWidth={4}
                  strokeColor="#111827"
                  canvasColor="#ffffff"
                  style={{ width: '100%', height: `${canvasHeight}px` }}
                  onStroke={() => publishDraft()}
                />
              </div>
            </div>
          </div>

          {canvasError ? <p role="alert" className="notice notice-error">{canvasError}</p> : null}
        </div>

        <div className="canvas-modal-actions">
          <button
            type="button"
            className="btn btn-primary"
            disabled={isSaving}
            onClick={handleDoneDrawing}
          >
            {isSaving ? 'Saving...' : 'Use this drawing'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default DrawingCanvasModal;
